import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import SignaturePad from '../components/SignaturePad';
import api from '../api/axios';

const Profile = () => {
    const { user, updateUser } = useAuth();
    const { config } = useConfig();

    useEffect(() => {
        if (!user?.id) return;
        
        const fetchUserProfile = async () => {
            try {
                const res = await api.get(`/users/${user.id}`);
                const fetchedUser = res.data;
                // Check if relevant data has changed to avoid unnecessary re-renders/updates
                if (fetchedUser.sala_id !== user.sala_id || fetchedUser.sala_name !== user.sala_name) {
                    updateUser({ ...user, sala_id: fetchedUser.sala_id, sala_name: fetchedUser.sala_name });
                }
            } catch (error) {
                console.error("Error fetching real-time profile data", error);
            }
        };

        const interval = setInterval(fetchUserProfile, 5000);
        return () => clearInterval(interval);
    }, [user, updateUser]);

    const primaryColor = config?.primaryColor || '#00595b';
    const secondaryColor = config?.secondaryColor || '#ff5874';
    const cardBorderColor = config?.cardBorderColor || secondaryColor;
    const titleColor = secondaryColor;

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editData, setEditData] = useState({
        name: user?.name || '',
        last_name: user?.lastName || user?.last_name || '',
        signature: user?.signature || null
    });
    const [error, setError] = useState('');

    const handleSave = async () => {
        setIsSaving(true);
        setError('');
        try {
            const res = await api.put('/users/profile/update', editData);
            updateUser(res.data.user);
            setIsEditing(false);
        } catch (err) {
            console.error("Error updating profile", err);
            setError('Error al actualizar el perfil');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="animate-fadeIn pb-32">
            <header className="mb-10 flex flex-col items-center text-center pt-8 relative">
                <div
                    className="w-24 h-24 rounded-[40px] flex items-center justify-center text-white text-4xl font-black mb-4 shadow-2xl relative"
                    style={{ backgroundColor: primaryColor }}
                >
                    {user?.name?.[0].toUpperCase()}
                    <div className="absolute -bottom-2 -right-2 bg-white p-2 rounded-full shadow-lg text-lg">✨</div>
                </div>
                {!isEditing ? (
                    <>
                        <h1 className="text-2xl sm:text-3xl font-black capitalize tracking-wide" style={{ color: titleColor }}>
                            {user?.name} {user?.lastName || user?.last_name}
                        </h1>
                        <p className="font-bold capitalize tracking-widest text-xs text-slate-300 mt-1">{user?.role}</p>
                    </>
                ) : (
                    <div className="flex flex-col gap-3 w-full max-w-xs mt-4">
                        <input
                            className="w-full bg-slate-100 border border-slate-200 p-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#ff5874] font-bold text-slate-800 shadow-inner"
                            placeholder="Nombre"
                            value={editData.name}
                            onChange={e => setEditData({ ...editData, name: e.target.value })}
                        />
                        <input
                            className="w-full bg-slate-100 border border-slate-200 p-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#ff5874] font-bold text-slate-800 shadow-inner"
                            placeholder="Apellidos"
                            value={editData.last_name}
                            onChange={e => setEditData({ ...editData, last_name: e.target.value })}
                        />
                    </div>
                )}
            </header>

            <div
                style={{ borderColor: cardBorderColor }}
                className="bg-white rounded-[40px] p-8 sm:p-10 border-2 shadow-2xl space-y-8 relative overflow-hidden max-w-2xl mx-auto"
            >
                <div className="absolute top-4 right-4 z-20">
                    {!isEditing ? (
                        <button
                            onClick={() => setIsEditing(true)}
                            style={{ backgroundColor: secondaryColor }}
                            className="text-white p-3 rounded-2xl text-[11px] font-black capitalize tracking-widest transition-all shadow-md hover:scale-105"
                        >
                            ✏️ Editar
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setIsEditing(false);
                                    setEditData({ name: user?.name, last_name: user?.lastName || user?.last_name, signature: user?.signature });
                                    setError('');
                                }}
                                className="bg-slate-700 text-white p-3 rounded-2xl text-[11px] font-black capitalize tracking-widest transition-all shadow-md"
                            >
                                ✕
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                style={{ backgroundColor: secondaryColor }}
                                className="text-white p-3 rounded-2xl text-[11px] font-black capitalize tracking-widest transition-all shadow-md hover:scale-105 disabled:opacity-50"
                            >
                                {isSaving ? '...' : '💾 Guardar'}
                            </button>
                        </div>
                    )}
                </div>
                {error && <p className="text-red-500 text-xs font-bold text-center absolute top-4 left-0 right-0">{error}</p>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10 pt-2">
                    <div className="space-y-1">
                        <label className="text-[11px] font-black capitalize tracking-wide pl-1 block text-left" style={{ color: secondaryColor }}>
                            Correo Electrónico
                        </label>
                        <p className="text-sm font-bold bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-slate-800 shadow-inner">
                            {user?.email}
                        </p>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[11px] font-black capitalize tracking-wide pl-1 block text-left" style={{ color: secondaryColor }}>
                            Identificación / CMP
                        </label>
                        <p className="text-sm font-bold bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-slate-800 shadow-inner">
                            {user?.identification || 'N/A'}
                        </p>
                    </div>
                    <div className="col-span-1 md:col-span-2 space-y-4 animate-fadeIn">
                        {user?.city && (
                            <div className="space-y-1">
                                <label className="text-[11px] font-black capitalize tracking-wide pl-1 block text-left" style={{ color: secondaryColor }}>
                                    Ciudad Registrada
                                </label>
                                <p className="text-sm font-bold bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-slate-800 shadow-inner flex items-center gap-2">
                                    <span>📍</span> {user.city}
                                </p>
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-700">Pre-Evento ("Asistiré"):</span>
                                <span className={`text-[10px] px-3 py-1 rounded-full font-black capitalize tracking-wider ${user?.asistire ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-500'}`}>
                                    {user?.asistire ? 'Confirmado ✓' : 'Pendiente'}
                                </span>
                            </div>
                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-700">Presencial ("En Evento"):</span>
                                <span className={`text-[10px] px-3 py-1 rounded-full font-black capitalize tracking-wider ${user?.asistencia_evento ? 'bg-purple-100 text-purple-800' : 'bg-slate-200 text-slate-500'}`}>
                                    {user?.asistencia_evento ? 'Registrado ✓' : 'Pendiente'}
                                </span>
                            </div>
                        </div>
                    </div>
                    {user?.sala_name && (
                        <div className="col-span-1 md:col-span-2 space-y-1 animate-fadeIn">
                            <label className="text-[11px] font-black capitalize tracking-wide pl-1 block text-left" style={{ color: secondaryColor }}>
                                Ubicación del Evento (Sala Registrada)
                            </label>
                            <p className="text-sm font-bold bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-slate-800 shadow-inner flex items-center gap-2">
                                <span>🏢</span> {user.sala_name}
                            </p>
                        </div>
                    )}
                </div>

                <div className="pt-8 border-t border-slate-200 flex flex-col items-center gap-6 relative z-10">
                    <p className="text-xs font-black capitalize tracking-widest text-center leading-relaxed text-slate-700">
                        Pase de Acceso <br />Virtual ID
                    </p>
                    <div className="w-56 bg-white p-5 rounded-[30px] shadow-lg border border-slate-100 flex flex-col items-center gap-3 relative overflow-hidden">
                        <div className="relative z-10 p-2 bg-white rounded-2xl">
                            {user?.id && (
                                <QRCodeSVG
                                    value={user.id.toString()}
                                    size={160}
                                    fgColor="#1c0042"
                                    level="H"
                                />
                            )}
                        </div>
                    </div>

                    {isEditing ? (
                        <div className="w-full max-w-md mt-4 flex flex-col items-center border-t border-slate-200 pt-6 relative z-10 space-y-4 px-4 sm:px-0">
                            <p className="text-[11px] font-black capitalize tracking-wide" style={{ color: secondaryColor }}>Modificar Firma</p>
                            <div className="w-full bg-slate-50 border border-slate-200 rounded-3xl p-2 shadow-inner">
                                <SignaturePad onSignatureChange={(sig) => setEditData({ ...editData, signature: sig })} />
                            </div>
                            {editData.signature && (
                                <img src={editData.signature} alt="Firma Nueva" className="max-h-16 object-contain mt-2" />
                            )}
                        </div>
                    ) : (
                        user?.signature && (
                            <div className="w-full max-w-md mt-4 flex flex-col items-center border-t border-slate-200 pt-6 relative z-10">
                                <p className="text-[11px] font-black capitalize tracking-wide mb-3" style={{ color: secondaryColor }}>Firma Registrada</p>
                                <img src={user.signature} alt="Firma del asistente" className="max-h-16 object-contain" />
                            </div>
                        )
                    )}
                    {!isEditing && <p className="text-xs font-bold text-slate-400 capitalize tracking-widest mt-1 relative z-10">Muestra este código para ingresar</p>}
                </div>
            </div>
        </div>
    );
};

export default Profile;
